import { FormList, formList } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { showNotification } from '@mantine/notifications';
import { X } from 'tabler-icons-react';
import { BusinessBankAccountTypes } from '../enums/bank.enums';
import {
  BusinessFileNames,
  BusinessFileNamesReadable,
} from '../enums/business-file-names';
import { BusinessTypes } from '../enums/business-type';
import {
  BaseOnboardingResponse,
  MerchantFileMetadata,
  OnboardingInformationByTab,
  RetrieveMerchantResponse,
  UpdateMerchantOnboardingDataRequest,
} from '../type/admin.types';
import { UpdateFormFile } from '../type/file.types';
import {
  ApiIncorporator,
  ApiSignatory,
  FormIncorporator,
  FormSignatory,
} from '../type/incorporator-signatory.types';
import { MerchantOnboardingFormValues } from '../type/merchant-onboarding-form-values.types';
import {
  RetrievedIncorporator,
  RetrievedSignatory,
} from '../type/onboard-merchant-request.types';
import { UpdateMerchantOnboardingFormValues } from '../type/update-merchant-onboarding-form-values.types';

export const setFormData = (
  form: UseFormReturnType<UpdateMerchantOnboardingFormValues>,
  data: RetrieveMerchantResponse,
  restructuredIncorporators: FormIncorporator[] | undefined,
  restructuredSignatories: FormSignatory[],
) => {
  let incorporators: FormList<FormIncorporator> | undefined;
  if (restructuredIncorporators) {
    incorporators = formList(restructuredIncorporators);
  } else {
    incorporators = undefined;
  }

  form.setValues({
    ...setGeneralInfo(data),

    ...setSignatoriesIncorporations(incorporators, restructuredSignatories),

    ...setBusinessInfo(data),

    ...setFormFiles(data.files),
    agreeTermsPrivacy: true,
  });
};

const setBusinessInfo = (data: RetrieveMerchantResponse) => {
  const banks = formList(
    data?.banks.map((bank) => {
      return {
        accountDepositTypeId: bank.accountDepositTypeId,
        accountTypeId: bank.accountTypeId || BusinessBankAccountTypes.SAVINGS, //this gets deleted when accountDepositTypeId!==2,
        bankName: bank.bankName || 'n/a',
        accountName: bank.accountName || 'n/a',
        accountNumber: bank.accountNumber || 'n/a',
        currencyId: bank.currencyId || undefined,
      };
    }),
  );

  return {
    copyProToRep: false,
    aFirstName: data?.representatives?.authorized?.firstName,
    aMiddleName: data?.representatives?.authorized?.middleName,
    isAMNameDisabled: false,
    aLastName: data?.representatives?.authorized?.lastName,
    aPosition: data?.representatives?.authorized?.position,
    aEmailAddress: data?.representatives?.authorized?.emailAddress,
    aContactNumber: data?.representatives?.authorized?.contactNumber,
    aDateOfBirth: new Date(data?.representatives?.authorized?.dateOfBirth),
    aTin: data?.representatives?.authorized?.tin
      ? data?.representatives?.authorized?.tin.slice(0, -3)
      : '',
    aSss: data?.representatives?.authorized?.sss,
    aMothersMaidenName:
      data?.representatives?.authorized?.mothersMaidenName || '',
    aGender: data?.representatives?.authorized?.gender || null,
    aAubNationalityCode:
      data?.representatives?.authorized?.aubNationalityCode || '',
    aAubDeviceId: data?.representatives?.authorized?.aubDeviceId || '',
    aAubIdCode: data?.representatives?.authorized?.aubIdCode || '',
    aAubIdNumber: data?.representatives?.authorized?.aubIdNumber || '',
    aAubRegionCode: data?.representatives?.authorized?.aubRegionCode || '',

    copyRepInfo: false,

    eFirstName: data?.representatives?.escalation?.firstName,
    eMiddleName: data?.representatives?.escalation?.middleName,
    isEMNameDisabled: false,
    eLastName: data?.representatives?.escalation?.lastName,
    ePosition: data?.representatives?.escalation?.position,
    eEmailAddress: data?.representatives?.escalation?.emailAddress,
    eContactNumber: data?.representatives?.escalation?.contactNumber,
    eDateOfBirth: data?.representatives?.escalation?.dateOfBirth
      ? new Date(data?.representatives?.escalation?.dateOfBirth)
      : null,
    eTin: data?.representatives?.escalation?.tin?.slice(0, -3),
    eSss: data?.representatives?.escalation?.sss,

    settlements: banks,

    isWantPackworksWallet: data?.isWantPackworksWallet,
    isWantPayMayaBankWallet: data?.isWantPayMayaWallet,
    isWantPayMayaBankAccount: data?.isWantPayMayaBankAccount,
  };
};
const setSignatoriesIncorporations = (
  incorporators: FormList<FormIncorporator> | undefined,
  restructuredSignatories: FormSignatory[],
) => {
  return {
    copyProToSignatory: false,
    incorporators: incorporators,
    signatories: formList(restructuredSignatories),
  };
};
const setGeneralInfo = (data: RetrieveMerchantResponse) => {
  const acquirerIds = data?.acquirers?.map((acquirer) => acquirer.id);
  return {
    businessTypeId: data?.businessTypeId,
    merchantName: data?.merchantName,
    merchantBusinessName: data?.legalName || '',
    businessHandle: data?.businessHandle,
    //if it is a business starter it has padded 000
    //which should be removed
    merchantCompanyTin:
      data?.businessTypeId === BusinessTypes.STARTER
        ? data?.tin.slice(0, -3)
        : data?.tin,

    mainOfficeContactNumber: data?.address?.office?.contactNumber,
    mainOfficeBldgNameNo: data?.address?.office?.buildingNameAndNumber || '',
    mainOfficeStreetAddress: data?.address?.office?.line1,
    mainOfficeBarangay: data?.address?.office?.barangay,
    mainOfficeCity: data?.address?.office?.city.toUpperCase(),
    mainOfficeProvince: data?.address?.office?.state.toUpperCase(),
    mainOfficePostalCode: data?.address?.office?.postalCode,

    registeredContactNumber: data?.address?.registered?.contactNumber,
    registeredBldgNameNo:
      data?.address?.registered?.buildingNameAndNumber || '',
    registeredStreetAddress: data?.address?.registered?.line1,
    registeredBarangay: data?.address?.registered?.barangay,
    registeredCity: data?.address?.registered?.city.toUpperCase(),
    registeredProvince: data?.address?.registered?.state.toUpperCase(),
    registeredPostalCode: data?.address?.registered?.postalCode,

    isBrickAndMortarStore: data?.isBrickAndMortarStore,
    industry: data?.industry,
    serviceDescription: data?.serviceDescription,
    modeOfPayment: data?.modeOfPaymentId,
    currency: data?.currencyId,
    estimatedMonthlyGrossTransaction:
      data?.estimatedMonthlyGrossTransaction || '',
    aubPartnerId: data?.aubPartnerId || '',
    acquirers: acquirerIds || [],

    businessWebsiteUrl: data?.websites?.businessWebsiteUrl || '',
    facebookUrl: data?.websites?.facebookUrl || '',
    twitterUrl: data?.websites?.twitterUrl || '',
    instagramUrl: data?.websites?.instagramUrl || '',
    tiktokUrl: data?.websites?.tiktokUrl || '',
    isBusinessWebsiteUnderDevelopment:
      data?.websites?.isBusinessWebsiteUnderDevelopment,
  };
};
export const setFormFiles = (data: MerchantFileMetadata[]) => {
  return {
    idsOfValidSignatories: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) => file.fileType === BusinessFileNames.VALID_IDS_OF_SIGNATORY,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    idsOfValidSignatoriesBack: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType === BusinessFileNames.VALID_IDS_OF_SIGNATORY_BACK,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    idsOfValidSignatoriesSignature: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType ===
            BusinessFileNames.VALID_IDS_OF_SIGNATORY_SIGNATURE,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),

    barangayBusinessPermit: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType === BusinessFileNames.BARANGAY_BUSINESS_PERMIT,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    barangayBusinessPermitBack: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType === BusinessFileNames.BARANGAY_BUSINESS_PERMIT_BACK,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    barangayBusinessPermitSignature: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType ===
            BusinessFileNames.BARANGAY_BUSINESS_PERMIT_SIGNATURE,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),

    storePhoto: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType === BusinessFileNames.STORE_PHOTO_WITH_STORE_NAME,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),

    mayorsOrBusinessPermit: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType === BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    mayorsOrBusinessPermitBack: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType === BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT_BACK,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    mayorsOrBusinessPermitSignature: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType ===
            BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT_SIGNATURE,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),

    birCert2303: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) => file.fileType === BusinessFileNames.BIR_CERTIFICATE_2303,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    birCert2303Back: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType === BusinessFileNames.BIR_CERTIFICATE_2303_BACK,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    birCert2303Signature: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType === BusinessFileNames.BIR_CERTIFICATE_2303_SIGNATURE,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),

    secCert: formList<UpdateFormFile | undefined>(
      data
        .filter((file) => file.fileType === BusinessFileNames.SEC_CERTIFICATE)
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    secCertBack: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) => file.fileType === BusinessFileNames.SEC_CERTIFICATE_BACK,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    secCertSignature: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType === BusinessFileNames.SEC_CERTIFICATE_SIGNATURE,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),

    aoiAndByLaws: formList<UpdateFormFile | undefined>(
      data
        .filter((file) => file.fileType === BusinessFileNames.AOI_AND_BY_LAWS)
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    aoiAndByLawsBack: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) => file.fileType === BusinessFileNames.AOI_AND_BY_LAWS_BACK,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    aoiAndByLawsSignature: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType === BusinessFileNames.AOI_AND_BY_LAWS_SIGNATURE,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),

    gis: formList<UpdateFormFile | undefined>(
      data
        .filter((file) => file.fileType === BusinessFileNames.GIS)
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    gisBack: formList<UpdateFormFile | undefined>(
      data
        .filter((file) => file.fileType === BusinessFileNames.GIS_BACK)
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    gisSignature: formList<UpdateFormFile | undefined>(
      data
        .filter((file) => file.fileType === BusinessFileNames.GIS_SIGNATURE)
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),

    secretarysCert: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) => file.fileType === BusinessFileNames.SECRETARYS_CERTIFICATE,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    secretarysCertBack: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType === BusinessFileNames.SECRETARYS_CERTIFICATE_BACK,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
    secretarysCertSignature: formList<UpdateFormFile | undefined>(
      data
        .filter(
          (file) =>
            file.fileType ===
            BusinessFileNames.SECRETARYS_CERTIFICATE_SIGNATURE,
        )
        .map((file) => ({ id: file.id, file: new File([], file.name) })),
    ),
  };
};

export const incorporatorsToApi = (
  form: UseFormReturnType<UpdateMerchantOnboardingFormValues>,
): ApiIncorporator[] | undefined => {
  const restructuredIncorporators = form.values.incorporators?.map(
    ({
      firstName,
      middleName,
      lastName,
      emailAddress,
      contactNumber,
      birthDate,
      ...address
    }) => {
      return {
        firstName,
        middleName,
        lastName,
        emailAddress,
        contactNumber,
        dateOfBirth: birthDate!,
        address: {
          city: address.city,
          line1: address.residentialAddress,
          buildingNameAndNumber: address.bldgNameNo,
          state: address.province,
          postalCode: address.postalCode,
          barangay: address.barangay,
        },
      };
    },
  );
  return restructuredIncorporators || undefined;
};

export const apiToIncorporators = (
  data: RetrievedIncorporator[] | undefined,
): FormIncorporator[] | undefined => {
  const restructuredIncorporators = data?.map(
    ({
      firstName,
      middleName,
      lastName,
      emailAddress,
      contactNumber,
      dateOfBirth,
      address,
    }) => {
      return {
        firstName,
        middleName,
        hasMiddleName: middleName !== 'N/A' && middleName !== '.',
        lastName,
        emailAddress,
        contactNumber,
        birthDate: new Date(dateOfBirth),
        city: address.city.toUpperCase(),
        residentialAddress: address.line1,
        bldgNameNo: address.buildingNameAndNumber || '',
        province: address.state.toUpperCase(),
        postalCode: address.postalCode,
        barangay: address.barangay,
      };
    },
  );
  return restructuredIncorporators;
};
export const signatoriesToApi = (
  form: UseFormReturnType<UpdateMerchantOnboardingFormValues>,
): ApiSignatory[] => {
  const restructuredSignatories = form.values.signatories.map(
    ({ firstName, middleName, lastName, position }) => {
      return {
        firstName,
        middleName,
        lastName,
        position,
      };
    },
  );
  return restructuredSignatories;
};

export const apiToSignatories = (
  data: RetrievedSignatory[],
): FormSignatory[] => {
  const restructuredSignatories = data.map(
    ({ firstName, middleName, lastName, position }) => {
      return {
        firstName,
        hasMiddleName: middleName !== 'N/A' && middleName !== '.',
        middleName: middleName!,
        lastName,
        position,
      };
    },
  );
  return restructuredSignatories;
};

export const createUpdateMerchantOnboardingDataRequest = (
  restructuredIncorporators: ApiIncorporator[] | undefined,
  restructuredSignatories: ApiSignatory[],
  values: UpdateMerchantOnboardingFormValues,
): UpdateMerchantOnboardingDataRequest => {
  const request: UpdateMerchantOnboardingDataRequest = {
    merchantName: values.merchantName,
    legalName: values.merchantBusinessName,
    businessHandle: values.businessHandle,
    tin: values.merchantCompanyTin,

    address: {
      office: {
        contactNumber: values.mainOfficeContactNumber,
        buildingNameAndNumber: values.mainOfficeBldgNameNo,
        line1: values.mainOfficeStreetAddress,
        barangay: values.mainOfficeBarangay,
        city: values.mainOfficeCity,
        state: values.mainOfficeProvince,
        postalCode: values.mainOfficePostalCode,
      },
      registered: {
        contactNumber: values.registeredContactNumber!,
        buildingNameAndNumber: values.registeredBldgNameNo!,
        line1: values.registeredStreetAddress!,
        barangay: values.registeredBarangay!,
        city: values.registeredCity!,
        state: values.registeredProvince!,
        postalCode: values.registeredPostalCode!,
      },
    },
    isBrickAndMortarStore: values.isBrickAndMortarStore,
    industry: values.industry,
    serviceDescription: values.serviceDescription,
    modeOfPaymentId: values.modeOfPayment,
    currencyId: values.currency,
    aubPartnerId: values.aubPartnerId,
    acquirerIds: values.acquirers,
    estimatedMonthlyGrossTransaction: values.estimatedMonthlyGrossTransaction,

    websites: {
      businessWebsiteUrl: values.businessWebsiteUrl,
      isBusinessWebsiteUnderDevelopment:
        values.isBusinessWebsiteUnderDevelopment,
      facebookUrl: values.facebookUrl,
      twitterUrl: values.twitterUrl,
      instagramUrl: values.instagramUrl,
      tiktokUrl: values.tiktokUrl,
    },

    incorporators: restructuredIncorporators,
    signatories: restructuredSignatories,

    representatives: {
      authorized: {
        firstName: values.aFirstName,
        middleName: values.aMiddleName,
        lastName: values.aLastName,
        position: values.aPosition,
        emailAddress: values.aEmailAddress,
        contactNumber: values.aContactNumber,
        dateOfBirth: new Date(values.aDateOfBirth!),
        tin: values.aTin,
        sss: values.aSss,
        mothersMaidenName: values.aMothersMaidenName,
        gender: values.aGender || undefined,
        aubNationalityCode: values.aAubNationalityCode,
        aubDeviceId: values.aAubDeviceId,
        aubIdCode: values.aAubIdCode,
        aubIdNumber: values.aAubIdNumber,
        aubRegionCode: values.aAubRegionCode,
      },
    },
    banks: values.settlements,

    isWantPackworksWallet: values.isWantPackworksWallet,
    isWantPayMayaWallet: values.isWantPayMayaBankWallet,
    isWantPayMayaBankAccount: values.isWantPayMayaBankAccount,
  };

  if (
    values.eFirstName &&
    values.eLastName &&
    values.eEmailAddress &&
    values.eContactNumber &&
    values.eDateOfBirth &&
    values.ePosition &&
    values.eTin &&
    values.eSss
  ) {
    request.representatives.escalation = {
      firstName: values.eFirstName,
      middleName: values.eMiddleName,
      lastName: values.eLastName,
      position: values.ePosition,
      emailAddress: values.eEmailAddress,
      contactNumber: values.eContactNumber,
      dateOfBirth: values.eDateOfBirth!,
      tin: values.eTin,
      sss: values.eSss,
    };
  }

  return request;
};

export const exposePageWithError = (
  form: UseFormReturnType<UpdateMerchantOnboardingFormValues>,
  generalHasError: boolean,
  setGeneralHasError: React.Dispatch<React.SetStateAction<boolean>>,
  incorporatorSignatoriesHasError: boolean,
  setIncorporatorSignatoriesHasError: React.Dispatch<
    React.SetStateAction<boolean>
  >,
  businessInfoHasErrors: boolean,
  setBusinessInfoHasErrors: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  if (
    form.errors?.merchantName ||
    form.errors?.merchantBusinessName ||
    form.errors?.businessHandle ||
    form.errors?.merchantCompanyTin ||
    form.errors?.mainOfficeContactNumber ||
    form.errors?.mainOfficeBldgNameNo ||
    form.errors?.mainOfficeStreetAddress ||
    form.errors?.mainOfficeBarangay ||
    form.errors?.mainOfficeCity ||
    form.errors?.mainOfficeProvince ||
    form.errors?.mainOfficePostalCode ||
    form.errors?.registeredContactNumber ||
    form.errors?.registeredBldgNameNo ||
    form.errors?.registeredStreetAddress ||
    form.errors?.registeredBarangay ||
    form.errors?.registeredCity ||
    form.errors?.registeredProvince ||
    form.errors?.registeredPostalCode ||
    form.errors?.isBrickAndMortarStore ||
    form.errors?.industry ||
    form.errors?.serviceDescription ||
    form.errors?.modeOfPayment ||
    form.errors?.businessWebsiteUrl ||
    form.errors?.facebookUrl ||
    form.errors?.twitterUrl ||
    form.errors?.instagramUrl ||
    form.errors?.tiktokUrl ||
    form.errors?.isBusinessWebsiteUnderDevelopment
  ) {
    if (!generalHasError) setGeneralHasError(true);
  } else if (generalHasError) {
    setGeneralHasError(false);
  }
  if (
    form.errors?.aFirstName ||
    form.errors?.aMiddleName ||
    form.errors?.isAMNameDisabled ||
    form.errors?.aLastName ||
    form.errors?.aPosition ||
    form.errors?.aEmailAddress ||
    form.errors?.aContactNumber ||
    form.errors?.aDateOfBirth ||
    form.errors?.aTin ||
    form.errors?.aSss ||
    form.errors?.aMothersMaidenName ||
    form.errors?.aGender ||
    form.errors?.aAubNationalityCode ||
    form.errors?.aAubDeviceId ||
    form.errors?.aAubIdCode ||
    form.errors?.aAubIdNumber ||
    form.errors?.aAubRegionCode ||
    form.errors?.copyRepInfo ||
    form.errors?.eFirstName ||
    form.errors?.eMiddleName ||
    form.errors?.isEMNameDisabled ||
    form.errors?.eLastName ||
    form.errors?.ePosition ||
    form.errors?.eEmailAddress ||
    form.errors?.eContactNumber ||
    form.errors?.eDateOfBirth ||
    form.errors?.eTin ||
    form.errors?.eSss ||
    // form.errors?.accountDepositTypeId ||
    // form.errors?.bankName ||
    // form.errors?.accountName ||
    // form.errors?.accountNumber ||
    form.errors?.isWantPackworksWallet ||
    form.errors?.isWantPayMayaBankWallet ||
    form.errors?.isWantPayMayaBankAccount ||
    form.errors?.idsOfValidSignatories ||
    form.errors?.mayorsOrBusinessPermit ||
    form.errors?.birCert2303 ||
    form.errors?.secCert ||
    form.errors?.gis ||
    form.errors?.aoiAndByLaws ||
    form.errors?.secretarysCert ||
    form.errors?.barangayBusinessPermit ||
    form.errors?.storePhoto
  ) {
    if (!businessInfoHasErrors) setBusinessInfoHasErrors(true);
  } else if (businessInfoHasErrors) {
    setBusinessInfoHasErrors(false);
  }
  if (
    JSON.stringify(form.errors).includes('incorporators.') ||
    JSON.stringify(form.errors).includes('signatories.')
  ) {
    if (!incorporatorSignatoriesHasError)
      setIncorporatorSignatoriesHasError(true);
  } else if (incorporatorSignatoriesHasError) {
    setIncorporatorSignatoriesHasError(false);
  }
};

export const validateUpdateFiles = (
  form: UseFormReturnType<UpdateMerchantOnboardingFormValues>,
): boolean => {
  const formFiles = getFileFromUpdateFormFile(form);
  const businessType = form.values.businessTypeId;
  let validFiles = true;
  let errMsg = '';

  // const permis = [
  //   BusinessFileNames.BARANGAY_BUSINESS_PERMIT,
  //   BusinessFileNames.SEC_CERTIFICATE,
  //   BusinessFileNames.BIR_CERTIFICATE_2303,
  //   BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT,
  // ];
  // const ownership = [BusinessFileNames.AOI_AND_BY_LAWS, BusinessFileNames.GIS];

  const noSolePermits =
    formFiles.mayorsOrBusinessPermit.length === 0 &&
    formFiles.birCert2303.length === 0;

  const noCorporationPartnershipPermits =
    formFiles.mayorsOrBusinessPermit.length === 0 &&
    formFiles.birCert2303.length === 0 &&
    formFiles.secCert.length === 0;

  const noOwnership =
    formFiles.aoiAndByLaws.length === 0 && formFiles.gis.length === 0;

  if (
    businessType === BusinessTypes.STARTER &&
    (formFiles.idsOfValidSignatories.length === 0 ||
      formFiles.storePhoto.length === 0 ||
      formFiles.barangayBusinessPermit.length === 0)
  ) {
    validFiles = false;
  } else if (
    businessType === BusinessTypes.SOLE_PROPRIETOR &&
    (formFiles.idsOfValidSignatories.length === 0 || noSolePermits)
  ) {
    validFiles = false;
  } else if (
    businessType === BusinessTypes.PARTNERSHIP &&
    (formFiles.idsOfValidSignatories.length === 0 ||
      noCorporationPartnershipPermits)
  ) {
    if (formFiles.idsOfValidSignatories.length === 0) {
      errMsg = `Please upload ${BusinessFileNamesReadable.VALID_IDS_OF_SIGNATORY}`;
    } else if (noCorporationPartnershipPermits) {
      errMsg = `Please upload any of the three: ${BusinessFileNamesReadable.MAYORS_OR_BUSINESS_PERMIT},
      ${BusinessFileNamesReadable.BIR_CERTIFICATE_2303},  ${BusinessFileNamesReadable.SEC_CERTIFICATE}`;
    }
    validFiles = false;
  } else if (
    businessType === BusinessTypes.CORPORATION &&
    (formFiles.idsOfValidSignatories.length === 0 ||
      noCorporationPartnershipPermits ||
      noOwnership ||
      formFiles.secretarysCert.length === 0)
  ) {
    if (formFiles.idsOfValidSignatories.length === 0) {
      errMsg = `Please upload ${BusinessFileNamesReadable.VALID_IDS_OF_SIGNATORY}`;
    } else if (noCorporationPartnershipPermits) {
      errMsg = `Please upload any of the three: ${BusinessFileNamesReadable.MAYORS_OR_BUSINESS_PERMIT},
      ${BusinessFileNamesReadable.BIR_CERTIFICATE_2303},  ${BusinessFileNamesReadable.SEC_CERTIFICATE}`;
    } else if (noOwnership) {
      errMsg = `Please upload any of the two:  ${BusinessFileNamesReadable.AOI_AND_BY_LAWS} 
      and ${BusinessFileNamesReadable.GIS}`;
    } else if (formFiles.secretarysCert.length === 0) {
      errMsg = `Please upload  ${BusinessFileNamesReadable.SECRETARYS_CERTIFICATE}`;
    }
  }

  if (!validFiles) {
    showNotification({
      id: 'failed-to-update-incomplete-files',
      message: errMsg,
      icon: <X size={16} />,
      color: 'red',
      autoClose: 10000,
    });
  }
  return validFiles;
};

export const getFileFromUpdateFormFile = (
  form:
    | UseFormReturnType<UpdateMerchantOnboardingFormValues>
    | UseFormReturnType<MerchantOnboardingFormValues>,
) => {
  const formValues = form.values;
  return {
    [BusinessFileNames.VALID_IDS_OF_SIGNATORY]: formList(
      formValues[BusinessFileNames.VALID_IDS_OF_SIGNATORY].map((file) => {
        return file?.file;
      }),
    ),
    [BusinessFileNames.BARANGAY_BUSINESS_PERMIT]: formList(
      formValues[BusinessFileNames.BARANGAY_BUSINESS_PERMIT].map((file) => {
        return file?.file;
      }),
    ),
    [BusinessFileNames.STORE_PHOTO_WITH_STORE_NAME]: formList(
      formValues[BusinessFileNames.STORE_PHOTO_WITH_STORE_NAME].map((file) => {
        return file?.file;
      }),
    ),
    [BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT]: formList(
      formValues[BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT].map((file) => {
        return file?.file;
      }),
    ),
    [BusinessFileNames.BIR_CERTIFICATE_2303]: formList(
      formValues[BusinessFileNames.BIR_CERTIFICATE_2303].map((file) => {
        return file?.file;
      }),
    ),
    [BusinessFileNames.SEC_CERTIFICATE]: formList(
      formValues[BusinessFileNames.SEC_CERTIFICATE].map((file) => {
        return file?.file;
      }),
    ),
    [BusinessFileNames.AOI_AND_BY_LAWS]: formList(
      formValues[BusinessFileNames.AOI_AND_BY_LAWS].map((file) => {
        return file?.file;
      }),
    ),
    [BusinessFileNames.GIS]: formList(
      formValues[BusinessFileNames.GIS].map((file) => {
        return file?.file;
      }),
    ),
    [BusinessFileNames.SECRETARYS_CERTIFICATE]: formList(
      formValues[BusinessFileNames.SECRETARYS_CERTIFICATE].map((file) => {
        return file?.file;
      }),
    ),
    [BusinessFileNames.VALID_IDS_OF_SIGNATORY_BACK]: formList(
      formValues[BusinessFileNames.VALID_IDS_OF_SIGNATORY_BACK].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.VALID_IDS_OF_SIGNATORY_SIGNATURE]: formList(
      formValues[BusinessFileNames.VALID_IDS_OF_SIGNATORY_SIGNATURE].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.BARANGAY_BUSINESS_PERMIT_BACK]: formList(
      formValues[BusinessFileNames.BARANGAY_BUSINESS_PERMIT_BACK].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.BARANGAY_BUSINESS_PERMIT_SIGNATURE]: formList(
      formValues[BusinessFileNames.BARANGAY_BUSINESS_PERMIT_SIGNATURE].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT_BACK]: formList(
      formValues[BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT_BACK].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT_SIGNATURE]: formList(
      formValues[BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT_SIGNATURE].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.BIR_CERTIFICATE_2303_BACK]: formList(
      formValues[BusinessFileNames.BIR_CERTIFICATE_2303_BACK].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.BIR_CERTIFICATE_2303_SIGNATURE]: formList(
      formValues[BusinessFileNames.BIR_CERTIFICATE_2303_SIGNATURE].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.SEC_CERTIFICATE_BACK]: formList(
      formValues[BusinessFileNames.SEC_CERTIFICATE_BACK].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.SEC_CERTIFICATE_SIGNATURE]: formList(
      formValues[BusinessFileNames.SEC_CERTIFICATE_SIGNATURE].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.AOI_AND_BY_LAWS_BACK]: formList(
      formValues[BusinessFileNames.AOI_AND_BY_LAWS_BACK].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.AOI_AND_BY_LAWS_SIGNATURE]: formList(
      formValues[BusinessFileNames.AOI_AND_BY_LAWS_SIGNATURE].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.GIS_BACK]: formList(
      formValues[BusinessFileNames.GIS_BACK].map((file) => file?.file),
    ),
    [BusinessFileNames.GIS_SIGNATURE]: formList(
      formValues[BusinessFileNames.GIS_SIGNATURE].map((file) => file?.file),
    ),
    [BusinessFileNames.SECRETARYS_CERTIFICATE_BACK]: formList(
      formValues[BusinessFileNames.SECRETARYS_CERTIFICATE_BACK].map(
        (file) => file?.file,
      ),
    ),
    [BusinessFileNames.SECRETARYS_CERTIFICATE_SIGNATURE]: formList(
      formValues[BusinessFileNames.SECRETARYS_CERTIFICATE_SIGNATURE].map(
        (file) => file?.file,
      ),
    ),
  };
};

export const retrieveOnboardingResponseToTab = (
  data: BaseOnboardingResponse,
  accountId: number,
): OnboardingInformationByTab => {
  const tabData: OnboardingInformationByTab = {
    general: {
      businessTypeId: data.businessTypeId,
      merchantName: data.merchantName,
      legalName: data.legalName,
      businessHandle: data.businessHandle,
      tin: data.tin,

      address: data.address,

      isBrickAndMortarStore: data.isBrickAndMortarStore,
      industry: data.industry,
      serviceDescription: data.serviceDescription,
      modeOfPaymentId: data.modeOfPaymentId,
      currencyId: data.currencyId,
      estimatedMonthlyGrossTransaction: data.estimatedMonthlyGrossTransaction,

      websites: data.websites,

      dateCreated: data.dateCreated,
      dateApproved: data.dateApproved,
      dateDeclined: data.dateDeclined,
      datePending: data.datePending,
      onboardingStatus: data.onboardingStatus,
      onboardingReferenceNumber: data.onboardingReferenceNumber,
      aubPartnerId: data.aubPartnerId,
      acquirers: data.acquirers,
    },
    signatories: data.signatories,
    incorporators: data.incorporators,

    businessInformation: {
      representatives: data.representatives,
      banks: data.banks,
      isWantPackworksWallet: data.isWantPackworksWallet,
      isWantPayMayaWallet: data.isWantPayMayaWallet,
      isWantPayMayaBankAccount: data.isWantPayMayaBankAccount,
    },

    documents: data.files,
    declinedReason: data.declinedReason,
    accountId,
    isActive: data.isActive,
    disabledReason: data.disabledReason,
    mdrFees: data.mdrFees,
  };

  return tabData;
};
